import { cn, Skeleton } from "@gotts.ai/ui";
import { ChevronDown, ChevronUp, Play, Send, Zap } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { decodeFunctionResult, encodeFunctionData } from "viem";

import { useWallet } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";

import { FunctionInput } from "./FunctionInput";
import { ResultDisplay } from "./ResultDisplay";
import { SendConfirmation } from "./SendConfirmation";
import { TransactionStatus } from "./TransactionStatus";
import { useGasEstimation } from "./useGasEstimation";
import {
  formatContractResult,
  getExampleSuggestion,
  isPayableFunction,
  isViewFunction,
  parseViemError,
} from "./utils";

import type { AbiFunction, FunctionResult } from "./types";
import type { KnownAddress } from "./useKnownAddresses";
import type { Address, Hex } from "viem";

type TxState =
  | { status: "pending"; hash: string }
  | { status: "confirmed"; hash: string; blockNumber: number }
  | { status: "failed"; hash: string; reason: string };

interface FunctionRowProps {
  fn: AbiFunction;
  contractAddress: Address;
  abi: readonly unknown[];
  isWrite: boolean;
  knownAddresses: KnownAddress[];
  history: FunctionResult[];
  onResult: (result: FunctionResult) => void;
  forceCollapsed?: boolean;
  abiKey?: string;
}

export function FunctionRow({
  fn,
  contractAddress,
  abi,
  isWrite,
  knownAddresses,
  history,
  onResult,
  forceCollapsed,
  abiKey,
}: FunctionRowProps) {
  const { request } = useRpcRequest();
  const {
    walletClient,
    address: walletAddress,
    chain,
    httpClient,
  } = useWallet();
  const [expanded, setExpanded] = useState(false);
  const [params, setParams] = useState<Record<string, string>>({});
  const [ethValue, setEthValue] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{
    message: string;
    details: string | null;
  } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [txState, setTxState] = useState<TxState | null>(null);

  const isView = isViewFunction(fn);
  const payable = isPayableFunction(fn);
  const effectiveExpanded = forceCollapsed ? false : expanded;

  // Example suggestion for this function
  const example = useMemo(
    () => (abiKey ? getExampleSuggestion(abiKey, fn, knownAddresses) : null),
    [abiKey, fn, knownAddresses],
  );

  const allParamsEmpty = fn.inputs.every(
    (input) => !params[input.name]?.trim(),
  );

  const applyExample = useCallback(() => {
    if (!example) return;
    const next: Record<string, string> = {};
    for (const input of fn.inputs) {
      next[input.name] = example.params[input.name] ?? "";
    }
    setParams(next);
  }, [example, fn.inputs]);

  // Gas estimation for write functions
  let encodedData: Hex | null = null;
  if (isWrite && expanded && walletAddress) {
    try {
      const args = fn.inputs.map((input) => {
        const val = params[input.name] ?? "";
        if (input.type.startsWith("uint") || input.type.startsWith("int")) {
          return BigInt(val || "0");
        }
        if (input.type === "bool") return val === "true";
        return val;
      });
      encodedData = encodeFunctionData({
        abi: abi as Parameters<typeof encodeFunctionData>[0]["abi"],
        functionName: fn.name,
        args,
      });
    } catch {
      // encoding failed, skip gas estimation
    }
  }

  const { gasEstimate } = useGasEstimation(
    walletAddress,
    contractAddress,
    encodedData,
    payable && ethValue
      ? BigInt(Math.floor(parseFloat(ethValue) * 1e18))
      : undefined,
  );

  const executeCall = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTxState(null);

    try {
      const args = fn.inputs.map((input) => {
        const val = params[input.name] ?? "";
        if (input.type.startsWith("uint") || input.type.startsWith("int")) {
          return BigInt(val || "0");
        }
        if (input.type === "bool") return val === "true";
        return val;
      });

      if (isWrite) {
        if (!walletClient || !walletAddress) {
          setError({ message: "Connect a wallet first", details: null });
          return;
        }
        const txParams: Record<string, unknown> = {
          chain,
          account: walletAddress,
          address: contractAddress,
          abi,
          functionName: fn.name,
          args,
        };
        if (payable && ethValue) {
          txParams.value = BigInt(Math.floor(parseFloat(ethValue) * 1e18));
        }
        const hash = await walletClient.writeContract(
          txParams as Parameters<typeof walletClient.writeContract>[0],
        );
        setTxState({ status: "pending", hash });

        // Wait for receipt
        try {
          const receipt = await httpClient.waitForTransactionReceipt({ hash });
          if (receipt.status === "success") {
            setTxState({
              status: "confirmed",
              hash,
              blockNumber: Number(receipt.blockNumber),
            });
            const formatted = `Tx confirmed: ${hash}`;
            setResult(formatted);
            onResult({
              value: formatted,
              raw: hash,
              timestamp: Date.now(),
            });
          } else {
            setTxState({
              status: "failed",
              hash,
              reason: "Transaction reverted",
            });
          }
        } catch {
          setTxState({
            status: "confirmed",
            hash,
            blockNumber: 0,
          });
          setResult(`Tx: ${hash}`);
          onResult({ value: `Tx: ${hash}`, raw: hash, timestamp: Date.now() });
        }
      } else {
        const data = encodeFunctionData({
          abi: abi as Parameters<typeof encodeFunctionData>[0]["abi"],
          functionName: fn.name,
          args,
        });
        const raw = await request("eth_call", [
          { to: contractAddress, data },
          "latest",
        ]);
        if (!raw || raw === "0x") {
          const formatted = "(call returned no data)";
          setResult(formatted);
          onResult({ value: formatted, raw: null, timestamp: Date.now() });
        } else {
          const decoded = decodeFunctionResult({
            abi: abi as Parameters<typeof decodeFunctionResult>[0]["abi"],
            functionName: fn.name,
            data: raw as `0x${string}`,
          });
          const formatted = formatContractResult(decoded, fn.outputs);
          setResult(formatted);
          onResult({ value: formatted, raw: decoded, timestamp: Date.now() });
        }
      }
    } catch (err) {
      setError(parseViemError(err));
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  }, [
    fn,
    params,
    contractAddress,
    abi,
    isWrite,
    walletClient,
    walletAddress,
    chain,
    ethValue,
    payable,
    request,
    httpClient,
    onResult,
  ]);

  const handleSendClick = () => {
    if (isWrite) {
      setShowConfirmation(true);
    } else {
      void executeCall();
    }
  };

  const hasNoInputs = fn.inputs.length === 0;
  const canQuickCall = isView && hasNoInputs;

  return (
    <div
      className={cn(
        "border-b border-[rgba(201,168,76,0.06)] last:border-b-0",
        "transition-colors duration-200",
      )}
    >
      {/* Function header — clickable */}
      <button
        type="button"
        onClick={() => {
          setExpanded(!expanded);
        }}
        className={cn(
          "group flex w-full items-center gap-2 px-5 py-2.5 text-left transition-all duration-200",
          isView
            ? "hover:bg-[rgba(52,209,106,0.04)]"
            : "hover:bg-[rgba(245,158,11,0.04)]",
          effectiveExpanded &&
            (isView
              ? "bg-[rgba(52,209,106,0.03)]"
              : "bg-[rgba(245,158,11,0.03)]"),
        )}
      >
        {/* Indicator dot */}
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-300",
            isView ? "bg-green-500/40" : "bg-amber-500/40",
            effectiveExpanded && (isView ? "bg-green-400" : "bg-amber-400"),
            "group-hover:scale-125",
          )}
        />
        <span
          className={cn(
            "font-mono text-xs font-semibold transition-colors",
            isView
              ? "text-green-400/80 group-hover:text-green-300"
              : "text-amber-400/80 group-hover:text-amber-300",
          )}
        >
          {fn.name}
        </span>
        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
          ({fn.inputs.map((i) => `${i.type} ${i.name}`).join(", ")})
        </span>
        {fn.outputs.length > 0 && (
          <span className="ml-auto bg-[rgba(201,168,76,0.05)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-muted)]">
            {fn.outputs.map((o) => o.type).join(", ")}
          </span>
        )}

        {/* Quick call button for no-input read functions */}
        {canQuickCall && !effectiveExpanded && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              void executeCall();
            }}
            className={cn(
              "flex h-5 w-5 items-center justify-center transition-all",
              "bg-green-500/10 text-green-400/60 hover:bg-green-500/20 hover:text-green-300",
              "opacity-0 group-hover:opacity-100",
            )}
            title="Quick call"
          >
            <Zap size={10} />
          </span>
        )}

        {effectiveExpanded ? (
          <ChevronUp
            size={14}
            className="shrink-0 text-[var(--color-text-muted)]"
          />
        ) : (
          <ChevronDown
            size={14}
            className="shrink-0 text-[var(--color-text-muted)] transition-transform group-hover:translate-y-0.5"
          />
        )}
      </button>

      {/* Expanded form */}
      {effectiveExpanded && (
        <div className="space-y-2.5 px-5 pb-4 pt-1">
          {fn.inputs.map((input) => (
            <FunctionInput
              key={input.name}
              name={input.name}
              type={input.type}
              value={params[input.name] ?? ""}
              onChange={(val) => {
                setParams((prev) => ({ ...prev, [input.name]: val }));
              }}
              knownAddresses={knownAddresses}
            />
          ))}

          {isWrite && payable && (
            <div className="flex items-center gap-2">
              <label className="flex w-28 shrink-0 items-baseline gap-1">
                <span className="text-xs font-normal text-[var(--color-text)]">
                  ETH Value
                </span>
                <span className="text-[10px] text-[var(--color-text-muted)]">
                  (payable)
                </span>
              </label>
              <input
                type="number"
                placeholder="0.0"
                value={ethValue}
                onChange={(e) => {
                  setEthValue(e.target.value);
                }}
                className="flex-1 border border-[rgba(201,168,76,0.12)] bg-transparent px-2 py-1.5 font-mono text-xs text-[var(--color-text)] outline-none focus:border-[var(--color-gold)]"
              />
            </div>
          )}

          {/* Example suggestion */}
          {example && allParamsEmpty && (
            <button
              type="button"
              onClick={applyExample}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1 font-mono text-[10px] transition-all",
                isView
                  ? "bg-green-500/8 text-green-400/70 hover:bg-green-500/15 hover:text-green-300"
                  : "bg-amber-500/8 text-amber-400/70 hover:bg-amber-500/15 hover:text-amber-300",
              )}
            >
              <Zap size={9} className="shrink-0" />
              Try: {example.label}
            </button>
          )}

          {/* Confirmation step for writes */}
          {showConfirmation && walletAddress ? (
            <SendConfirmation
              fn={fn}
              params={params}
              ethValue={ethValue}
              gasEstimate={gasEstimate}
              walletAddress={walletAddress}
              onConfirm={executeCall}
              onCancel={() => {
                setShowConfirmation(false);
              }}
              loading={loading}
            />
          ) : (
            <div className="flex items-center gap-3 pt-1">
              {loading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <button
                  type="button"
                  onClick={handleSendClick}
                  disabled={loading}
                  className={cn(
                    "octa-sm flex items-center gap-1.5 px-4 py-2 font-mono text-xs font-medium transition-all duration-200",
                    isWrite
                      ? "bg-[rgba(201,168,76,0.15)] text-[var(--color-gold)] hover:bg-[rgba(201,168,76,0.25)] active:scale-[0.97]"
                      : "bg-[rgba(52,209,106,0.1)] text-green-400 hover:bg-[rgba(52,209,106,0.18)] active:scale-[0.97]",
                    "disabled:opacity-40",
                  )}
                >
                  {isWrite ? (
                    <Send size={11} className="shrink-0" />
                  ) : (
                    <Play size={11} className="shrink-0" />
                  )}
                  {isWrite ? "Send" : "Call"}
                </button>
              )}
              {isWrite && gasEstimate !== null && !showConfirmation && (
                <span className="font-mono text-[9px] text-[var(--color-text-muted)]">
                  ~{gasEstimate.toLocaleString()} gas
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transaction status */}
      {txState && <TransactionStatus txState={txState} />}

      {/* Persistent result/error */}
      <ResultDisplay result={result} error={error} history={history} />
    </div>
  );
}
